import React, { useCallback, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line import/order
import './_YatPage.scss';

import { type EmojiId as EmojiIdWasm } from '@tari-labs/emojis-wasm-default';
import md5 from 'md5';
import { Col, Container, Hidden, Row } from 'react-grid-system';
import { useTranslation } from 'react-i18next';
import { useMatch, useParams } from 'react-router-dom';

import { EmojiIDNotFoundOverlay } from '@pkg/components';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { type ApiResult, type EidCategories, EidCategory, type EmojiId, type Tag } from '@app/client';
import { YatPageVersion } from '@app/client/common/structs/yatPage/yat-page';
import config from '@app/config';
import { LinkItem, track } from '@app/helpers';
import { PathParams } from '@app/routes/types';
import { getEmojiTags, loadWasms, updateEmojiTag } from '@app/store/actions/emoji';
import { addNotification } from '@app/store/actions/notification';
import { setYatPageVersion } from '@app/store/slices';
import { WasmType } from '@app/store/types/emoji';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import EditableHeadline from '../../components/YatPage/EditableHeadline/EditableHeadline';
import UsersYat from '../../components/YatPage/UsersYat/UsersYat';
import YatLinks from '../../components/YatPage/YatLinks';
import YatLogo from '../../components/YatPage/YatLogo/YatLogo';
import YatPageBio from '../../components/YatPage/YatPageBio';
import YatPageNavigation from '../../components/YatPage/YatPageNavigation/YatPageNavigation';

const { privacyLink, termsLink } = config;

interface InternalState {
    currentEmojiTags?: Tag[];
    insertHashes?: string[];
    deleteHashes?: string[];
}

enum YatStatus {
    Valid,
    Invalid,
    Loading,
}

const YatPage = () => {
    const { t } = useTranslation(['yat_page', 'translation', 'common']);
    const { [PathParams.Yat]: yat } = useParams();

    const isEditLinksMatch = useMatch(`/:${[PathParams.Yat]}/editlinks`);
    const isEditLinkMatch = useMatch(`/:${[PathParams.Yat]}/edit`);
    const routeIsEdit = !!(isEditLinkMatch || isEditLinksMatch);

    const [emojiInstance, setEmojiInstance] = useState<EmojiIdWasm>();
    const [yatStatus, setYatStatus] = useState<YatStatus>(YatStatus.Loading);
    const [editable, setEditable] = useState<boolean>(false);
    const [userOwnsPage, setUserOwnsPage] = useState<boolean>(false);
    const [currentEmojiTags, setCurrentEmojiTags] = useState<Tag[]>([]);
    const [remoteTagHashes, setRemoteTagHashes] = useState<string[]>([]);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [invalidData, setInvalidData] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [displayEmoji, setDisplayEmoji] = useState<EmojiId>(yat as string);
    const [canonicalYat, setCanonicalYat] = useState<EmojiId>(yat as string);
    const [unmounted, setUnmounted] = useState<boolean>(false);

    const yatPageVersion = useAppSelector((state) => state.yatPage.yatPageVersion);
    const emojiWasm = useAppSelector((state) => state.emoji.wasm);
    const typesWasm = useAppSelector((state) => state.emoji.types);
    const emojis = useEmojisStore((state) => state.emojis);

    const dispatch = useAppDispatch();
    const [is404Overlay, set404Overlay] = useState(yatPageVersion === YatPageVersion.NOT_FOUND || false);

    const stateRef = useRef<InternalState>({});
    const sendUpdatesToServer = useCallback(async (): Promise<boolean> => {
        setIsLoading(true);
        const { insertHashes = [], deleteHashes = [], currentEmojiTags = [] } = stateRef.current;
        const deleteServerHashes: string[] = [];
        // Remove the local hashes
        const insertServerData: EidCategories[] = [];
        // TODO this isn't required but needs to be tested first, use the below line instead
        // const deleteServerHashes: string[] = deleteHashes;
        deleteHashes.forEach((hash) => {
            const index = insertHashes.indexOf(hash);
            if (index > -1) {
                //Remove from the list of inserts
                insertHashes.splice(index, 1);
            } else if (hash.indexOf('temp') !== 0) {
                deleteServerHashes.push(hash);
            }
        });
        insertHashes.forEach((hash) => {
            const item = currentEmojiTags.find((t) => {
                const linkItem = new LinkItem(t.data, t.hash);
                return t.hash === hash && !linkItem.isEmpty();
            });
            if (item) {
                insertServerData.push({
                    data: item.data,
                    tag: item.tag,
                } as unknown as EidCategories);
            }
        });
        if (insertServerData.length || deleteServerHashes.length) {
            const deleteRedirectTag = currentEmojiTags.filter((t) => t.tag === EidCategory.REDIRECT).map((t) => t.hash);
            deleteRedirectTag.forEach((deleteHash) => deleteServerHashes.push(deleteHash));

            const dispatchedPromise = dispatch(
                updateEmojiTag(yat as string, insertServerData, deleteServerHashes),
            ) as unknown as Promise<ApiResult<EmojiId[]>>;
            return dispatchedPromise
                .then((r) => {
                    if (r.ok) {
                        stateRef.current = { ...stateRef.current, insertHashes: [], deleteHashes: [] };
                        return true;
                    }
                    dispatch(
                        addNotification({
                            message: 'There was an error',
                            severity: 'error',
                        }),
                    );
                    return false;
                })
                .catch((e) => {
                    // TODO Add an error warning
                    dispatch(
                        addNotification({
                            message: e.error,
                            severity: 'error',
                        }),
                    );
                    return false;
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
        return true;
    }, [yat, dispatch, setIsLoading]);

    const updateTagsQueue = useCallback(
        (eidCategory: EidCategory, data?: string, removeHashes: string[] = [], ignoreDirtyState = false) => {
            if (!editable || isLoading) {
                return;
            }
            const { insertHashes = [], deleteHashes = [], currentEmojiTags = [] } = stateRef.current;
            const deleteRemoteHashes: string[] = [];
            const tags = removeHashes.length
                ? currentEmojiTags.filter((tag) => {
                      const removeThisHash = removeHashes.indexOf(tag.hash) > -1;
                      // Check if this is a remote hash and should be sent to the server.
                      if (removeThisHash && remoteTagHashes.indexOf(tag.hash) > -1) {
                          deleteRemoteHashes.push(tag.hash);
                      }
                      return !removeThisHash;
                  })
                : currentEmojiTags;
            let newInsertHashes = [...insertHashes];
            if (data) {
                let hash = '';
                try {
                    hash = 'v' + typesWasm?.itemHash(new typesWasm.Category(eidCategory), data) || '';
                    newInsertHashes = [...new Set([...insertHashes, hash])];
                } catch (e) {
                    hash = 'temp' + md5(data);
                }
                tags.push({ tag: eidCategory, data, hash: hash });
            }

            // this is currently only being set for link items and is being used for that, if this changes, YatNav will have to be updated
            setInvalidData(
                !!tags.find((t) => {
                    const linkItem = new LinkItem(t.data, t.hash);
                    return !linkItem.isEmpty() && t.hash.indexOf('temp') === 0;
                }),
            );

            const newDeleteHashes = [...new Set([...deleteHashes, ...(deleteRemoteHashes || [])])];
            const newTags = [...tags];
            setCurrentEmojiTags(newTags);
            const newData = {
                insertHashes: newInsertHashes,
                deleteHashes: newDeleteHashes,
                currentEmojiTags: newTags,
            };
            setIsDirty(!ignoreDirtyState && (newData.insertHashes.length > 0 || newData.deleteHashes.length > 0));
            stateRef.current = { ...stateRef.current, ...newData };
        },
        [isLoading, remoteTagHashes, setIsDirty, typesWasm, editable],
    );

    // Always start the wasm load immediately.
    useEffect(() => {
        dispatch(loadWasms(WasmType.EmojiWasm));
    }, [dispatch]);

    // Load the wasm and validate the Yat
    useEffect(() => {
        if (yat && emojiWasm) {
            try {
                const emojiInstance = new emojiWasm.EmojiId(yat, config.maxLengthYat);
                if (emojiInstance) {
                    setEmojiInstance(emojiInstance);
                    const displayYat = emojiInstance.preferred();
                    setDisplayEmoji(displayYat);
                    setCanonicalYat(emojiInstance.canonical());
                } else {
                    throw Error('Invalid Yat');
                }
                setYatStatus(YatStatus.Valid);
            } catch (_) {
                setYatStatus(YatStatus.Invalid);
            }
        }
    }, [yat, emojiWasm]);

    // Set whether the page is owned by the current user and they are editing it
    useEffect(() => {
        if (!emojiInstance || !emojiWasm) {
            return;
        }
        const canonicalYat = emojiInstance.canonical();
        const userOwnsPage = !!emojis?.find(
            (e) => new emojiWasm.EmojiId(e, config.maxLengthYat)?.canonical() === canonicalYat,
        );
        setUserOwnsPage(userOwnsPage);
        const editable = routeIsEdit && userOwnsPage;
        setEditable(editable);
        if (editable) {
            dispatch(loadWasms(WasmType.EmojiTypesWasm));
        }
    }, [emojiWasm, emojiInstance, emojis, currentEmojiTags, routeIsEdit, dispatch]);

    useEffect(() => {
        return () => {
            setUnmounted(true);
        };
    }, []);

    useEffect(() => {
        if (!unmounted) {
            document.body.style.backgroundColor = '#ffffff';
        }

        return () => {
            document.body.style.backgroundColor = '';
        };
    }, [unmounted]);

    useEffect(() => {
        // Generate the modified version of the Yat
        if (currentEmojiTags) {
            const preferred = currentEmojiTags.find((item) => item.tag === EidCategory.EMOJI_PREFERRED);
            if (preferred && emojiWasm) {
                let preferredForm = preferred.data;
                try {
                    preferredForm =
                        new emojiWasm.EmojiId(preferredForm, config.maxLengthYat)?.preferred() || (yat as string);
                } catch (_) {
                    preferredForm = yat as string;
                }
                setDisplayEmoji(preferredForm);
            }

            if (emojiInstance && !currentEmojiTags.find((item) => item.tag === EidCategory.REDIRECT) && !userOwnsPage) {
                track('yat_url_viewed', { emoji_id: emojiInstance.preferred() });
            }
        }
    }, [emojiWasm, yat, emojiInstance, currentEmojiTags, userOwnsPage]);

    // Fetch the tags for the Yat from the server
    useEffect(() => {
        setIsLoading(true);
        if (unmounted || !yat) {
            return;
        }
        getEmojiTags(yat, [])
            .then((r) => {
                if (r.ok && r.value && r.value.result && !unmounted) {
                    const emojiTags = r.value.result;
                    setCurrentEmojiTags(emojiTags);
                    setRemoteTagHashes(emojiTags.map((tag) => tag.hash));
                    stateRef.current.currentEmojiTags = emojiTags;
                } else {
                    if (!r.ok && r.error.toString().includes(`EmojiId 404`)) {
                        dispatch(setYatPageVersion(YatPageVersion.NOT_FOUND));
                        set404Overlay(true);
                    }
                }
            })
            .catch((e) => {
                if (e.error) {
                    dispatch(
                        addNotification({
                            message: e.error,
                            severity: 'error',
                        }),
                    );
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
        return () => {
            setIsLoading(false);
        };
    }, [dispatch, yat, unmounted]);

    useEffect(() => {
        const stopUnload = (event) => {
            // Cancel the event as stated by the standard.
            event.preventDefault();
            // Chrome requires returnValue to be set.
            event.returnValue = '';
            return '';
        };
        if (isDirty && editable) window.addEventListener('beforeunload', stopUnload);
        else window.removeEventListener('beforeunload', stopUnload);
        return () => {
            window.removeEventListener('beforeunload', stopUnload);
        };
    }, [isDirty, editable]);

    if (is404Overlay) {
        return <EmojiIDNotFoundOverlay emojiId={canonicalYat} />;
    }

    return (
        <div className={`yat-page ${!editable ? 'live-page' : ''}`}>
            {editable && (
                <Container fluid style={{ padding: 0 }}>
                    <YatPageNavigation
                        isDirty={isDirty}
                        editable={editable}
                        invalidData={invalidData}
                        saveChanges={sendUpdatesToServer}
                        emojiID={yat as string}
                    />
                </Container>
            )}
            <div className="scroll-fade top" />

            <Container className="yat-page__content">
                <Row justify="between">
                    <Col xs={12} sm={12} md={12} lg={5} xl={5} className="side-panel left">
                        <Row align={'center'}>
                            <Col>
                                <UsersYat yat={yatStatus === YatStatus.Invalid ? '🤔' : displayEmoji} />
                            </Col>
                        </Row>
                        <Row align={'center'}>
                            <Col>
                                <EditableHeadline
                                    updateTagsQueue={updateTagsQueue}
                                    editable={editable}
                                    currentEmojiTags={currentEmojiTags}
                                    isLoading={isLoading}
                                    setIsDirty={setIsDirty}
                                />
                            </Col>
                        </Row>
                        <Row align={'center'}>
                            <Col>
                                <YatPageBio
                                    updateTagsQueue={updateTagsQueue}
                                    editable={editable}
                                    currentEmojiTags={currentEmojiTags}
                                    isLoading={isLoading}
                                />
                            </Col>
                        </Row>
                        <Row style={{ flexGrow: 1 }} align="end">
                            <Col>
                                <div className="desktop-yat-logo-container">
                                    <YatLogo />
                                </div>
                                <Hidden xs sm md>
                                    <div className="tos-links">
                                        <div className="link">
                                            <a href={termsLink}>{t('common:terms_link_terms')}</a>
                                        </div>
                                        <div className="link">
                                            <a href={privacyLink}>{t('common:terms_link_privacy_agreement')}</a>
                                        </div>
                                    </div>
                                </Hidden>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={7} xl={7} className="side-panel right">
                        <Row align={'center'} nogutter={true}>
                            <Col offset={{ lg: 2, xl: 2 }}>
                                {(!editable || typesWasm) && (
                                    <YatLinks
                                        emojiId={yat as string}
                                        setIsDirty={setIsDirty}
                                        isLoading={isLoading}
                                        updateTagsQueue={updateTagsQueue}
                                        editable={editable}
                                        currentEmojiTags={currentEmojiTags || []}
                                        invalidEmojiId={yatStatus === YatStatus.Invalid}
                                        emojiIdNotFound={false}
                                    />
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Hidden lg xl xxl>
                                <div className="mobile-yat-logo-container">
                                    <YatLogo />
                                </div>
                                <div className="tos-links">
                                    <div className="link">
                                        <a href={termsLink}>{t('common:terms_link_terms')}</a>
                                    </div>
                                    <div className="link">
                                        <a href={privacyLink}>{t('common:terms_link_privacy_agreement')}</a>
                                    </div>
                                </div>
                            </Hidden>
                        </Row>
                    </Col>
                </Row>
            </Container>
            <div className="scroll-fade bottom" />
        </div>
    );
};

export default YatPage;
